import React, { useCallback, useEffect, useState } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "../../common/alert";
import { SubmitHandler, useForm } from "react-hook-form";
import { PromptDTO } from "@src/model/prompt";
import { usePromptAPI } from "@src/api/prompt_api";

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "Never";
  try {
    return new Date(dateString).toLocaleString();
  } catch (e) {
    return "Invalid Date";
  }
};

const PromptIndex: React.FC = () => {
  const [data, setData] = useState<PromptDTO[]>([]);
  const [status, setStatus] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { getAllPrompts, createPrompt, deletePrompt } = usePromptAPI();
  const navigate = useNavigate();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<PromptDTO>();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const prompts = await getAllPrompts();
      setData(prompts);
    } catch (error: any) {
      setStatus({ type: "error", message: "Failed to load prompts: " + error.message });
    } finally {
      setLoading(false);
    }
  }, [getAllPrompts]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (id: string | undefined) => {
    if (id) {
      navigate(`/prompts/edit/${id}`);
    }
  };
  const handleDelete = (id: string | undefined) => {
    if (id) {
      if(window.confirm("Are you sure you want to delete this prompt?")) {
        deletePrompt(id).then(() => {
            setStatus({ type: "success", message: "Prompt deleted successfully." });
            fetchData();
        }).catch((error: any) => {
            setStatus({ type: "error", message: "Failed to delete prompt: " + error.message });
        })
      }
    }
  };

  const onCreateSubmit: SubmitHandler<PromptDTO> = async (formData) => {
    setStatus(null);
    setLoading(true);
    try {
      await createPrompt(formData);
      setStatus({ type: "success", message: "Prompt created successfully." });
      reset();
      fetchData();
    } catch (error: any) {
      setStatus({ type: "error", message: "Failed to create prompt: " + error.message });
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "Prompt",
      selector: (row: PromptDTO) => row.prompt || '',
      sortable: true,
      wrap: true
    },
    {
      name: "Entity Type",
      selector: (row: PromptDTO) => row.entity_type || '',
      sortable: true
    },
    {
      name: "Entity ID",
      selector: (row: PromptDTO) => row.entity_id || '',
      sortable: true
    },
    {
      name: "Status",
      selector: (row: PromptDTO) => row.active ? "Active" : "Inactive",
      sortable: true,
      cell: (row: PromptDTO) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
          {row.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Created At",
      selector: (row: PromptDTO) => formatDate(row.created_at),
      sortable: true
    },
    {
      name: "Actions",
      button: true,
      cell: (row: PromptDTO) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEdit(row.id)}
            disabled={!!actionLoading}
            className="p-1 bg-blue-500 text-white rounded hover:bg-blue-700 text-xs disabled:opacity-50"
            aria-label="Edit"
          >
            Edit
          </button>
          <button
              onClick={() => handleDelete(row.id)}
              disabled={!!actionLoading}
              className="p-1 bg-red-500 text-white rounded hover:bg-red-700 text-xs disabled:opacity-50"
              aria-label="Delete"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppContainer>
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-1">Prompts</h1>
        <p className="text-sm text-gray-600 mb-4">Manage AI prompts for the system.</p>

        {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4" />}

        <form onSubmit={handleSubmit(onCreateSubmit)} className="bg-white p-4 rounded shadow border mb-6">
          <h2 className="text-lg font-semibold mb-3">Create New Prompt</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
              <textarea
                id="prompt"
                {...register("prompt", { required: "Prompt is required" })}
                className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.prompt ? "border-red-500" : "border-gray-300"}`}
                placeholder="Enter your prompt text"
                rows={4}
              />
              {errors.prompt && <p className="text-red-500 text-xs mt-1">{String(errors.prompt.message)}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="entity_type" className="block text-sm font-medium text-gray-700 mb-1">Entity Type (Optional)</label>
                <select
                    id="entity_type"
                    {...register("entity_type", {
                      onChange:(e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }
                    })}
                    className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.entity_type ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value=''>-- Select Entity Type --</option>
                  <option value="LICENSABLE_PROPERTY">LICENSABLE_PROPERTY</option>
                  <option value="USER_PRODUCT">USER_PRODUCT</option>
                  <option value="BRAND">BRAND</option>
                </select>
                {errors.entity_type?.message && <p className="text-red-500 text-xs mt-1">{String(errors.entity_type.message)}</p>}
              </div>

              <div>
                <label htmlFor="entity_id" className="block text-sm font-medium text-gray-700 mb-1">Entity ID</label>
                <input
                  type="number"
                  id="entity_id"
                  {...register("entity_id", { valueAsNumber: true })}
                  className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.entity_id ? "border-red-500" : "border-gray-300"}`}
                  placeholder="Enter entity ID"
                />
                {errors.entity_id && <p className="text-red-500 text-xs mt-1">{String(errors.entity_id.message)}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register("active")}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="ml-auto px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
              >
                + Create Prompt
              </button>
            </div>
          </div>
        </form>

        <div className="border rounded shadow-sm">
          <DataTable
            columns={columns}
            data={data}
            progressPending={loading}
            progressComponent={<div className="py-10 text-center">Loading...</div>}
            noDataComponent={<div className="py-10 text-center text-gray-500">No prompts found.</div>}
            pagination
            highlightOnHover
            striped
            customStyles={{
              headCells: { style: { fontSize: "13px", fontWeight: "bold", color: "#4a5568", backgroundColor: "#f7fafc" } },
              cells: { style: { fontSize: "13px" } },
            }}
          />
        </div>
      </div>
    </AppContainer>
  );
};

export default PromptIndex;
